// AdminHeader: Professional header for admin panel
import React, { useEffect, useState } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, Chip, Stack } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import NotificationDropdown from './NotificationDropdown';
import ProfileDropdown from './ProfileDropdown';
import ThemeToggle from './ThemeToggle';
import { InputBase } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useContext } from 'react';
import { SettingsContext } from '../context/SettingsContext';
import api from '../services/api';
import { useProfile } from '../context/ProfileContext';

// helper to read current user safely
const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch (e) { return {}; }
};

function AdminHeader() {
  const settings = useContext(SettingsContext);
  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/,'');
  const logoSrc = settings?.logoUrl || `${API_BASE}/api/public/logo`;
  const platformName = settings?.platformName || 'Expo & Conference Platform';
  const stored = getCurrentUser();
  const { profile: serverProfile } = useProfile() || {};
  const [user, setUser] = useState(serverProfile || stored || {});
  const [perms, setPerms] = useState(serverProfile?.permissions || serverProfile?.perms || []);

  useEffect(() => {
    if (serverProfile) {
      setUser(serverProfile);
      setPerms(serverProfile.permissions || serverProfile.perms || []);
      try { localStorage.setItem('user', JSON.stringify(serverProfile)); } catch (e) {}
      return;
    }
    // no server profile; attempt one-off fetch to enrich local user
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/api/user/profile').then(res => {
        const profile = res.data || {};
        setUser(profile);
        try { localStorage.setItem('user', JSON.stringify(profile)); } catch (e) {}
        const p = profile.permissions || profile.perms || [];
        setPerms(Array.isArray(p) ? p : (typeof p === 'string' ? p.split(/[,;|]/).map(x=>x.trim()) : []));
      }).catch(() => {});
    }
  }, [serverProfile]);

  const displayName = user?.name || 'Admin';
  // Normalize role for display: if role is array or CSV, prefer 'admin' when present
  const normalizeRoleForDisplay = (r) => {
    if (!r) return 'admin';
    let arr = [];
    if (Array.isArray(r)) arr = r.map(x => String(x).toLowerCase());
    else if (typeof r === 'string') arr = r.split(/[,;|]/).map(x => String(x).trim().toLowerCase());
    else arr = [String(r).toLowerCase()];
    if (arr.includes('admin')) return 'admin';
    return arr[0] || 'admin';
  };
  const role = normalizeRoleForDisplay(user?.role);
  const prettyRole = role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Admin';
  return (
    <AppBar position="static" color="inherit" elevation={2} sx={{ mb: 2, borderRadius: 2, boxShadow: 3 }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: 72 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img src={logoSrc} alt={platformName} style={{ height: 44, width: 44, objectFit: 'contain', marginRight: 12, borderRadius: 4, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 1, color: 'primary.main' }}>
              {platformName} <span style={{ fontWeight: 400, color: '#888', fontSize: '1.1rem', marginLeft: 8 }}>| Admin Panel</span>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{displayName} • {prettyRole}</Typography>
            {perms && perms.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                {perms.slice(0,4).map((p, i) => <Chip key={i} label={p} size="small" />)}
                {perms.length > 4 && <Chip label={`+${perms.length - 4} more`} size="small" />}
              </Stack>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ bgcolor: 'background.paper', px: 1, borderRadius: 1, display: 'flex', alignItems: 'center', minWidth: { xs: 100, md: 200 } }}>
            <SearchIcon color="action" />
            <InputBase placeholder="Search…" sx={{ ml: 1, flex: 1 }} />
          </Box>
          <NotificationDropdown />
          <ProfileDropdown />
          <ThemeToggle />
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default AdminHeader;
